import { APhotoUploadTask, uploadPhoto } from "@/api/UploadApi";
import apiClient, { strapiApiUrl } from "@/api/api";
import useImmobilieHelper from "@/composables/Property/useImmobilieHelper";
import { SortTerm, SortTermActive } from "@/composables/Sort/SortTerms";
import { AppPlatform } from '@/composables/useAppInfo';
import useDownloadHelper from "@/composables/useDownloadHelper";
import { useStore } from "@/composables/useTypedStore";
import useUser from "@/composables/useUser";
import BestandsaufnahmeModel from "@/models/ba/models/bestandsaufnahme.model";
import Immobilie from "@/models/immobilie.model";
import { ImmobilieStatus } from "@/models/immobilie/interfaces/IImmobilie";
import { APhoto } from "@/models/photo/a-photo.model";
import User from "@/models/user";
import { instanceOfPhoto } from "@/utilities/get-media-url";
import { Monitoring } from "@/utilities/monitoring";
import { AppLauncher } from "@capacitor/app-launcher";
import { Clipboard } from '@capacitor/clipboard';
import { Share } from '@capacitor/share';
import { generateUUID } from "@ionic/cli/lib/utils/uuid";
import { alertController, toastController } from '@ionic/vue';
import QueryString from 'qs';
import { computed } from "vue";
import { environment } from "../../../environments/environment";

export function useProperties() {

    const store = useStore();
    const immoPhotoUid = generateUUID();
    const { user } = useUser()
    const currentProject = computed(() => store.state.user.currentUserProject);
    const featureFlags = computed(() => user.value!.organisation?.featureFlags)
    const { isMobile } = useDownloadHelper();
    const networkConnected = computed(() => store.state.app.networkConnected);

    /**
     * Build the query for the backend.
     */
    const getNextQuery = async () => {
        const pageSize = 1000000;
        await Immobilie.dispatch('setPage', (Immobilie.getters("page") + 1));
        const sortBy = Immobilie.getters("sortBy") as SortTermActive;

        const searchString = Immobilie.getters("searchTerm");
        const statusFilter = Immobilie.getters("statusFilter");
        const personFilter = Immobilie.getters("personFilter");
        const organizationUsers = store.state.user.organizationUsers ?? [];

        const query: any = {
            pagination: {
                page: Immobilie.getters("page"),
                pageSize: pageSize
            }
        };

        if (sortBy) {
            const sortObj = { [sortBy.fieldName]: sortBy.orderBy }

            if (sortBy.subObject) {
                const splittedKeys = sortBy.subObject?.split('.');
                if (splittedKeys.length === 1) query.sort = { [sortBy.subObject]: sortObj}
                if (splittedKeys.length === 2) query.sort = { [splittedKeys[0]]: { [splittedKeys[1]]: sortObj } }
            } else {
                query.sort = sortObj;
            }
        }

        if (!query.filters) { query.filters = { $and: [] } }

        if ((searchString ?? "") != "") {

            query.filters = {
                $and: [{
                    $or: [
                        { name: { $containsi: searchString } },
                        { eigentuemer: { $containsi: searchString } },
                        { strasse: { $containsi: searchString } },
                        { plz: { $containsi: searchString } },
                        { stadt: { $containsi: searchString } },
                        { externeObjektNr: { $containsi: searchString } },
                        { baujahr: { $containsi: searchString } },
                    ]
                }]
            }
        }

        if (statusFilter.length > 0) {
            if (!query.filters) {
                query.filters = { $and: [] }
            }
            query.filters.$and.push({
                $or: statusFilter.map((status: ImmobilieStatus) =>
                    ({ status: status ? { $containsi: status } : { $null: true } }))
            });
        }

        if (personFilter.length > 0) {
            if (!query.filters) {
                query.filters = { $and: [] }
            }
            query.filters.$and.push({
                $or: personFilter
                    .map((person: string) => organizationUsers.find((user: User) => user.username === person)?.id ?? 0)
                    .filter((id: number) => id !== 0)
                    .map((id: number) => ({ verwalters: { id } }))
            });
        }

        return query;
    }


    /**
     * Stringifies the query
     */
    const getQueryString = async () => {
        return QueryString.stringify(await getNextQuery())
    }

    /**
     * Sortable items
     */
    const supportedSortTerms: SortTerm[] = [
        { label: 'Leitungsanlage', fieldName: 'stadt'},
        { label: 'Mastnummer', fieldName: 'name'},
        { label: 'Objekt Nr.', fieldName: 'externeObjektNr'}
    ];

    /**
     * Load Properties from IndexedDB but do not store them in vuex directly. We don't know if we want to show those properties at this state due to search/filter function;
     */
    const loadPersistedProperties = async (): Promise<Immobilie[]> => {
        await Immobilie.dispatch("loadPersistedProperties");
        return Immobilie.getters("persistedProperties")
    }

    /**
     * Persist immobilie with id
     */
    const downloadImmobilie = async (id: number) => {
        Monitoring.consoleOnly("IMMO UPDATE")

        const immobilie: Immobilie | undefined = Immobilie.find(id) as (Immobilie | undefined);

        if (immobilie) {
            immobilie.isDownloaded = true;
            await Immobilie.dispatch('$updateLocally', { data: immobilie });
        } else {
            Monitoring.error("downloadImmobilie: immobilie is empty and could not be downloaded.")
        }
    }

    /**
     * Fetch immobilies from backend
     * Called after login, when on /bas or /properties page
     */
    const loadImmobilienPreviews = async (query?: string) => {
        const persistedProperties = await loadPersistedProperties();

        Immobilie.commit((state) => {
            state.searchResultsCount = null;
            state.totalSearchResultsCount = null;
        });

        try {
            if (!Immobilie.getters("sortBy")) { await Immobilie.dispatch("setSortBy", 'externeObjektNr'); }

            const queryString = `/immobilies?${query ? query : await getQueryString()}`;
            Monitoring.setContext("Query", { queryString });
            const res = await Immobilie.api().get(queryString, { save: false, params: { projectId: String(currentProject.value.id) } });

            const resProperties = (res.getDataFromResponse() as any);

            if (!resProperties.results || resProperties.results.length === 0) {
                Monitoring.consoleOnly("Fetched Properties: empty result")
                Immobilie.commit((state) => {
                    state.searchResultsCount = 0;
                    state.totalSearchResultsCount = 0;
                });
                return;
            }

            const fetchedProperties = resProperties.results as Immobilie[];

            const pagination = resProperties.pagination;

            const retrievedCount = pagination.page >= pagination.pageCount ? pagination.total : pagination.page * pagination.pageSize;
            Immobilie.commit((state) => {
                state.searchResultsCount = retrievedCount;
                state.totalSearchResultsCount = pagination.total;
            });

            const propsToPush: Immobilie[] = [];
            // Monitoring.consoleOnly("MYLOG fetchedProperties", fetchedProperties);

            for (const prop of fetchedProperties) {
                const foundPersisted = persistedProperties?.find((localProp: any) => localProp.id === prop.id);
                if (foundPersisted) {
                    const immobilie = new Immobilie(prop);

                    if (immobilie && foundPersisted.updatedAt < immobilie.updatedAt) {
                        /*
                         * Persisted Property is not up-to-date anymore
                         */

                        immobilie.isDownloaded = true;

                        // Monitoring.consoleOnly("local prop found, replace with newer one", prop, immobilie);
                        // // TODO replace with full
                        // console.warn("TODO - replace with full prop instead of preview prop (local prop found, replace with newer one)")
                        // await Immobilie.dispatch('$updateLocally', { data: immobilie });
                        await fetchFullProperty(immobilie.id);
                    } else {
                        // Monitoring.consoleOnly("persisted prop found", foundPersisted);
                        /**
                         * Persisted property is up-to-date, so push persisted.
                         */
                        propsToPush.push(foundPersisted);
                    }
                } else {
                    /**
                     * No persisted prop found for this id.
                     * But is the property already loaded in vuex? if not, push.
                     */
                    // Monitoring.consoleOnly("no persisted prop found. ", prop);
                    const foundLocal = Immobilie.all().find((localProp: any) => localProp.id === prop.id);
                    if (!foundLocal) {
                        // Monitoring.consoleOnly("pushing property as no local found", prop)
                        propsToPush.push(prop);
                    }
                }
            }

            // TODO: handle edgecase when there are properties persisted on the client database that are not in the server database anymore
            // should not happen by user interaction, but is possible to occur by backend actions e.g. deleting properties
            // the check should maybe happen at application startup, not at every fetch
            await Immobilie.insert({ data: propsToPush });

            // Monitoring.consoleOnly("page count", resProperties.pagination.pageCount);

            //Post status updates
            Immobilie.commit((state) => {
                state.page = pagination.page;
                state.pageCount = pagination.pageCount;
                state.propertiesLoaded = true;
            });

        } catch (error: any) {
            // On Network Error, load Immobilies from local storage
            if (error.message === "Network Error") {
                const immobilies = await loadPersistedProperties();
                await Immobilie.insert({ data: [...immobilies] });

                Immobilie.commit((state) => {
                    state.page = 1;
                    state.pageCount = 1;
                    state.propertiesLoaded = true;
                 });
            } else {
                Monitoring.chainError("loadImmobilienPreviews: Error loading immobilies", error);
            }
        } finally {
            Monitoring.clearContexts();
        }
    }

    /**
     * Load full immobilie
     */
    const fetchFullProperty = async (id: any) => {
        try {
            const res = await Immobilie.api().get(`/immobilies/${id}`, { save: false, params: { projectId: currentProject.value.id } })
            const immo = (res.getDataFromResponse() as any).data;

            await Immobilie.insertOrUpdate({ data: immo });
            await downloadImmobilie(id);

            return immo;
        } catch (error: any) {
            if (error.message === "Network Error") {
                Monitoring.consoleOnly("Cannot load full immobilie, no connection!");
            } else {
                throw error;
            }
        }
    }

    /**
     * Check if there are some ba's left that are offline. If not, remove from downloaded.
     */
    const considerRemoveImmobilieFromDownloaded = async (immobilieId: number) => {
        const bas = BestandsaufnahmeModel.query().where("immobilie", immobilieId).all();

        const localBas = bas.filter(ba => ba.isDownloaded || ba.isLocal);

        if (!localBas || localBas?.length == 0) {  // delete Immobilie only if no other downloaded BA has same Immobilie
            const im = Immobilie.find(immobilieId);
            if (!im) {
                console.warn("considerRemoveImmobilieFromDownloaded: Could not find immobilie", immobilieId);

                return;
            }

            im!.isDownloaded = false;

            // await Immobilie.dispatch('removeFromPersistedProperties', im);
            await Immobilie.dispatch('$deleteFromLocal', im.id);
            await Immobilie.insertOrUpdate({ data: im as any });
        }
    }

    const getImmoPhototask = ( property: Immobilie, type: string, image: APhoto ): APhotoUploadTask => {

        return {
            field: type,
            image,
            ref: type,
            api: "api::immobilie.immobilie",
            uid: immoPhotoUid,
            status: instanceOfPhoto(property.vorschaubild) ? "preparing" : "success",
            instanceId: property.id,
            fileName: property.id + Date.now().toString() + ".jpeg"
        }
    }

    const createVorschauBildTasks = async () => {
        const persistedProperties = await loadPersistedProperties();
        const vorschauBildTasks: APhotoUploadTask[] = [];
        const bildTasks: APhotoUploadTask[] = [];
        const updatedPropertyIds: number[] = [];

        persistedProperties.forEach((property: Immobilie) => {
            // @ts-ignore
            if ( property.vorschaubild && property.vorschaubild.webPath ) {
                vorschauBildTasks.push( getImmoPhototask( property, "vorschaubild", property.vorschaubild ) );
                updatedPropertyIds.push(property.id);
            }
            if ( property.bilder && property.bilder.length && property.bilder.length > 0 ) {
                property.bilder.forEach((bild: any) => {
                    if ( bild.webPath) {
                        bildTasks.push( getImmoPhototask( property, "bilder", bild ) );

                        const existingPersistedBaIndex = updatedPropertyIds.findIndex(( id: number ) => id === property.id );

                        if ( existingPersistedBaIndex === -1 ) {
                            updatedPropertyIds.push( property.id );
                        }
                    }
                });

            }
        });

        return {
            vorschauBildTasks,
            bildTasks,
            updatedPropertyIds
        }
    }

    const sendImmophotos = async ( tasks: APhotoUploadTask[] ) => {
        return await Promise.all(
            tasks.map((el: APhotoUploadTask) => {
                return uploadPhoto(
                    // @ts-ignore
                    el.image,
                    el.api,
                    el.instanceId?.toString(),
                    el.field,
                    el.fileName,
                    "immobilie"
                    );
            })
        );
    }
    const sendPersistedImages = async () => {
        const {
            vorschauBildTasks,
            bildTasks,
            updatedPropertyIds
        } = await createVorschauBildTasks();
        let uploadVorschauBildResults: any = [];

        try {
            uploadVorschauBildResults  = await sendImmophotos( vorschauBildTasks );

        } catch (error: any) {
            throw Monitoring.toBubbleUp("sendImmophotos vorschaubild error", error);
        }

        try {
            await sendImmophotos( bildTasks );

        } catch (error: any) {
            throw Monitoring.toBubbleUp("sendImmophotos bilder error", error);
        }

        try {
            await Promise.all(
                uploadVorschauBildResults.map((el: any, index: number) => {
                    return Immobilie.api().post("/immobilie/addImmoPhoto", {
                        propertyId: vorschauBildTasks[index].instanceId?.toString(),
                        imageId: el.data[0].id
                    });
                })
            );
        } catch (error: any) {
            throw Monitoring.toBubbleUp("addImmoPhoto error", error);
        }

        try {
            await Promise.all(
                updatedPropertyIds.map((id: number) => {
                    return fetchFullProperty(id);

                })
            );
        }
        catch (error: any) {
            throw Monitoring.toBubbleUp("fetchFullProperty error", error);
        }

    }

    /**
     * Changes the person of a property and fetches and downloads the full property afterwards.
     * @param property
     * @param t
     * @returns
     */
    const changePerson = async (property: Immobilie, t: any) => {
        if(!featureFlags?.value.property?.changePerson) return
        const response = (await apiClient.get(`${strapiApiUrl}/users?filters[organisation][id][$eq]=${user.value!.organisation.id}`)).data
        const users = response.map((item: any) => ({
            label: item.username,
            type: 'radio',
            value: item.id,
            checked: property.verwalters.filter((propertyItem: any) => propertyItem.username === item.username).length > 0
        }))

        const alertButtons = [
            {
            text: t('immobilie.buttons.confirm'),
            handler: async (userId: string) => {
                await Immobilie.api().put(`/immobilies/${property.id}?projectId=${currentProject.value.id}`, {
                save: true,
                data: {
                    verwalters: {
                    disconnect: property.verwalters.map((item: any) => { return { id: item.id }}),
                    connect: [
                        { id: userId }
                    ]
                    }
                }
                })
                fetchFullProperty(property.id);
            }
            },
            t('immobilie.buttons.cancel')
        ];

        const alert = await alertController.create({
            header: t('immobilie.updatePerson'),
            buttons: alertButtons,
            //@ts-ignore
            inputs: users
        })

        await alert.present();
    }

    /**
     * Changes the status of a property and fetches and downloads the full property afterwards.
     * @param property
     * @param t
     * @returns
     */
    const changeStatus = async (property: Immobilie, t: any) => {
        if(!featureFlags?.value.property?.changeStatus) return
        const alertButtons = [
          {
            text: t('immobilie.buttons.confirm'),
              handler: async (status: ImmobilieStatus) => {
                await Immobilie.api().put(`/immobilies/${property.id}?populate=verwalters&projectId=${currentProject.value.id}`, {
                  save: true,
                  data: { status: status }
                })
                fetchFullProperty(property.id);
              }
          },
          t('immobilie.buttons.cancel')
        ];
        const statuses = [
          {
            label: ImmobilieStatus.ABGESCHLOSSEN,
            type: 'radio',
            value: ImmobilieStatus.ABGESCHLOSSEN,
            checked: property.status === ImmobilieStatus.ABGESCHLOSSEN
          },
          {
            label: ImmobilieStatus.ANGELEGT,
            type: 'radio',
            value: ImmobilieStatus.ANGELEGT,
            checked: property.status === ImmobilieStatus.ANGELEGT
          },
          {
            label: ImmobilieStatus.ARCHIVIERT,
            type: 'radio',
            value: ImmobilieStatus.ARCHIVIERT,
            checked: property.status === ImmobilieStatus.ARCHIVIERT
          },
          {
            label: ImmobilieStatus.FREIGEGEBEN,
            type: 'radio',
            value: ImmobilieStatus.FREIGEGEBEN,
            checked: property.status === ImmobilieStatus.FREIGEGEBEN
          },
          {
            label: ImmobilieStatus.GEPLANT,
            type: 'radio',
            value: ImmobilieStatus.GEPLANT,
            checked: property.status === ImmobilieStatus.GEPLANT
          },
          {
            label: ImmobilieStatus.IN_DURCHFUEHRUNG,
            type: 'radio',
            value: ImmobilieStatus.IN_DURCHFUEHRUNG,
            checked: property.status === ImmobilieStatus.IN_DURCHFUEHRUNG
          },
        ];
        const alert = await alertController.create({
          header: t('immobilie.updateStatus'),
          buttons: alertButtons,
          //@ts-ignore
          inputs: statuses
        })
        await alert.present();
    }

    const uploadPropertyVorschaubild = async (property: any, images: Array<any>) => {
        const data = property
        data.attributes["id"] = property.id
        const { uploadVorschaubild } = useImmobilieHelper(data.attributes as Immobilie );
        await uploadVorschaubild(images[0], property.id )
        return
    }

    const generatePropertyShareLink = (propertyId: string) => {
        const serverUrl = environment.SERVER_URL
        const url = serverUrl + `/property/${propertyId}`

        return url
    }

    const redirectToMap = async ({ lon, lat }: { lon: number, lat: number }) => {
        const googleUrl = `comgooglemaps://?q=${lat},${lon}&zoom=14&views=traffic`;
        const appleUrl = `https://maps.apple.com/?q=${lat},${lon}&z=14`;
        if ( lat && lon) {
          if ( isMobile.value ) {
            try {
              const isGoogleMapsAvailable = await AppLauncher.canOpenUrl({ url: googleUrl });
              const isAppleMapsAvailable = await AppLauncher.canOpenUrl({ url: appleUrl });
              if ( isGoogleMapsAvailable.value ) {
                await AppLauncher.openUrl( { url: googleUrl } );
              } else if ( isAppleMapsAvailable.value ) {
                await AppLauncher.openUrl( { url: appleUrl } );
              } else {
                //TODO: send to sentry
                Monitoring.consoleOnly("Can't use neither Google Maps nor Apple Maps");
              }
            } catch (error: any) {
              //TODO: send to sentry
              Monitoring.consoleOnly('Error opening map on mobile:', error);
            }
          } else if ( networkConnected.value ) {
            try {
              await AppLauncher.openUrl( { url: `https://maps.google.com/?q=@${lat},${lon}` } );
            } catch (error: any) {
              //TODO: send to sentry
              Monitoring.consoleOnly('Error opening map on desktop:', error);
            }
          }
        }
    };

    const handlePropertyShare = async ( propertyId: string, t: any ) => {
        const url = generatePropertyShareLink(propertyId)

        if(store.state.app.appPlatform === AppPlatform.desktop) {
          await Clipboard.write({
            string: url
          })

          const toast = await toastController.create({
              message: t('property.urlCopied'),
              duration: 3000,
              cssClass: 'copyUrlToast'
          })

          await toast.present();
        } else {
          await Share.share({
            title: t('property.shareProperty.title'),
            text: t('property.shareProperty.text'),
            url: url
          });
        }
      };

    return {
        loadImmobilienPreviews,
        considerRemoveImmobilieFromDownloaded,
        downloadImmobilie,
        fetchFullProperty,
        supportedSortTerms,
        sendPersistedImages,
        changePerson,
        changeStatus,
        uploadPropertyVorschaubild,
        generatePropertyShareLink,
        redirectToMap,
        handlePropertyShare
    };
}








